import React, {memo, useCallback, useEffect, useMemo} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import makeStyles from '@mui/styles/makeStyles';
import AgyleDictionaryField from '../../../components/ui/selector/agyleDictionaryField';
import {
    calendarViewOpen,
    changeFilterValue,
    filterAppointmentSlots,
    filterAutoLocationSelect,
    filterCliniciansState,
    filterClinicNamesState,
    filterLocationsState,
    filterPractitionerRoles,
    filterSessionNamesState,
    isBarContextState,
    listSortState,
    listViewOpen,
    selectedFilters,
    setAutoLocationSelect,
    setFacility,
    setListSort,
} from '../../../store/reducers/appointments';
import { SLOT_STATUSES } from '../utils/constants/appointmentSessionConstants';
import AgyleMultiselectAutocomplete from '../../../components/ui/selector/agyleMultiselectAutocomplete';
import _isEmpty from 'lodash/isEmpty';

export const SORT_BY_OPTIONS = [
    {
        id: 0,
        __description: 'Name: Ascending',
        value: ['patient.familyName', 'patient.firstName'],
        direction: ['asc', 'asc'],
        default: true,
        name: 'name',
    },
    {
        id: 1,
        __description: 'Name: Descending',
        value: ['patient.familyName', 'patient.firstName'],
        direction: ['desc', 'desc'],
        default: false,
        name: 'name',
    },
    { id: 2, __description: 'Date: Latest', value: 'startDate', direction: 'asc', default: false, name: 'date' },
    { id: 3, __description: 'Date: Oldest', value: 'startDate', direction: 'desc', default: false, name: 'date' },
    { id: 3, __description: 'Status', value: 'status', direction: 'asc', default: false, name: 'status' },
];

const useStyles = makeStyles((theme) => ({
    clearButton: {
        color: theme.palette.primary.main,
    },
    right: {
        gap: 10,
        flexGrow: 1,
        marginLeft: 'auto',
        flexWrap: 'nowrap',
        alignItems: 'center',
        justifyContent: 'flex-end',
        alignSelf: 'end',
    },
    filterItemContainer: {
        maxWidth: 300,
    },
    dropDownSortStyle: {
        fontFamily: 'Roboto',
        fontSize: 12,
        fontWeight: 400,
    },
}));

export const STATUS_FILTER_DICT = [
    {
        __id: SLOT_STATUSES.NOT_REGISTERED.value,
        __description: SLOT_STATUSES.NOT_REGISTERED.name,
        value: SLOT_STATUSES.NOT_REGISTERED.value,
    },
    { __id: SLOT_STATUSES.ARRIVED.value, __description: SLOT_STATUSES.ARRIVED.name, value: SLOT_STATUSES.ARRIVED.value },
    { __id: SLOT_STATUSES.CALLED.value, __description: SLOT_STATUSES.CALLED.name, value: SLOT_STATUSES.CALLED.value },
    { __id: SLOT_STATUSES.BEEING_SEEN.value, __description: SLOT_STATUSES.BEEING_SEEN.name, value: SLOT_STATUSES.BEEING_SEEN.value },
    { __id: SLOT_STATUSES.DEPARTED.value, __description: SLOT_STATUSES.DEPARTED.name, value: SLOT_STATUSES.DEPARTED.value },
    { __id: SLOT_STATUSES.DNA.value, __description: SLOT_STATUSES.DNA.name, value: SLOT_STATUSES.DNA.value },
];

const FilterSection = memo(({ patientContext }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const selectedFiltersSelector = useSelector(selectedFilters);
    const isBarsContextActive = useSelector(isBarContextState);
    const filterLocations = useSelector(filterLocationsState);
    const filterClinicNames = useSelector(filterClinicNamesState);
    const filterSessionNames = useSelector(filterSessionNamesState);
    // const filterSpecialties = useSelector(filterSpecialtiesState);
    const filterClinicians = useSelector(filterCliniciansState);
    const filterRoles = useSelector(filterPractitionerRoles);
    const autoLocationSelect = useSelector(filterAutoLocationSelect);
    const isCalendarViewOpen = useSelector(calendarViewOpen);
    const isListViewOpen = useSelector(listViewOpen);
    const sortValue = useSelector(listSortState);

    const handleChangeFilter = (key, value) => {
        if (key === 'clinics') {
            let hospitalId = null;
            value.forEach((v) => {
                if (hospitalId) return;
                if (!_isEmpty(v?.sess?.[0]?.clinic?.hospital?.href)) {
                    const temp = v?.sess?.[0]?.clinic?.hospital?.href.split('/');
                    hospitalId = temp[2];
                }
            });

            if (hospitalId) dispatch(setFacility(hospitalId));
        }
        dispatch(changeFilterValue(value, key));
    };

    /**
     * Effect hook to handle auto location selection
     * When filterLocations and autoLocationSelect are available, it finds the location filter
     * and applies it using handleChangeFilter, then resets the autoLocationSelect
     * @param {Array} filterLocations - Array of available location filters
     * @param {string} autoLocationSelect - ID of location to auto select
     */
    useEffect(() => {
        if (!_isEmpty(filterLocations) && autoLocationSelect) {
            const findFilter = filterLocations.find((f) => f.__id === autoLocationSelect);
            handleChangeFilter('locations', findFilter ? [findFilter] : []);
            dispatch(setAutoLocationSelect(null));
        }
    }, [filterLocations, autoLocationSelect]);

    const renderOptionLabel = useCallback((option) => {
        return `Sort by: ${option.__description}`;
    }, []);
    const _changeSortState = (event, value) => {
        dispatch(setListSort(value));
        dispatch(filterAppointmentSlots());
    };

    // useEffect(() => {
    //     dispatch(filterAppointmentSlots());
    // }, [memoedFiltersValue, memoedSortValue]);

    return (
        // <Grid
        //     container
        //     item
        //     xs={12}
        //     sx={{mb: 1, pt: 0}}
        //     style={{alignItems: 'center', gap: 8, flexWrap: 'nowrap'}}
        // >
        <Grid item xs={12} container alignItems="center" className="scroll-normal-white" gap={2} flexWrap="nowrap" sx={{ py: 1 }}>
            <Grid item xs={6} sm={4} md={2} className={classes.filterItemContainer}>
                {/*<AgyleMultiselectDropdown*/}
                <AgyleMultiselectAutocomplete
                    options={filterLocations}
                    onChange={(val) => handleChangeFilter('locations', val)}
                    label={t('Location')}
                    value={selectedFiltersSelector?.locations}
                    size="medium"
                    hideOption
                    pathToItemValue="__id"
                    fullObjecValue={true}
                    noBorder={false}
                    style={{ minWidth: 175 }}
                    showSelectAll={false}
                    selectNoneLabel={'Clear'}
                />
            </Grid>
            <Grid item xs={6} sm={4} md={2} className={classes.filterItemContainer}>
                {/*<AgyleMultiselectDropdown*/}
                <AgyleMultiselectAutocomplete
                    options={filterClinicNames}
                    onChange={(val) => handleChangeFilter('clinics', val)}
                    label={t(isBarsContextActive ? 'Healthcare Service' : 'Clinic Name')}
                    value={selectedFiltersSelector?.clinics}
                    size="medium"
                    hideOption
                    pathToItemValue="__id"
                    fullObjecValue={true}
                    noBorder={false}
                    style={{ minWidth: 175 }}
                    showSelectAll={false}
                    selectNoneLabel={'Clear'}
                />
            </Grid>
            <Grid item xs={6} sm={4} md={2} className={classes.filterItemContainer}>
                <AgyleMultiselectAutocomplete
                    options={filterSessionNames}
                    onChange={(val) => handleChangeFilter('sessions', val)}
                    label={t('Session Name')}
                    value={selectedFiltersSelector?.sessions}
                    size="medium"
                    hideOption
                    pathToItemValue="__id"
                    fullObjecValue={true}
                    noBorder={false}
                    style={{ minWidth: 175 }}
                    showSelectAll={false}
                    selectNoneLabel={'Clear'}
                    autocomplete={true}
                />
            </Grid>
            {/*<Grid*/}
            {/*    item*/}
            {/*    xs={6}*/}
            {/*    sm={4}*/}
            {/*    md={2}*/}
            {/*    className={classes.filterItemContainer}*/}
            {/*>*/}
            {/*    /!*<AgyleMultiselectDropdown*!/*/}
            {/*    <AgyleMultiselectAutocomplete*/}
            {/*        options={filterSpecialties}*/}
            {/*        onChange={(val) => handleChangeFilter('specialties', val)}*/}
            {/*        label={t('Specialty')}*/}
            {/*        value={selectedFiltersSelector?.specialties}*/}
            {/*        size="medium"*/}
            {/*        noBorder={false}*/}
            {/*        pathToItemValue='__id'*/}
            {/*        fullObjecValue={true}*/}
            {/*        style={{minWidth: 175}}*/}
            {/*        showSelectAll={false}*/}
            {/*        selectNoneLabel={"Clear"}*/}
            {/*    />*/}
            {/*</Grid>*/}
            <Grid item xs={6} sm={4} md={2} className={classes.filterItemContainer}>
                {/*<AgyleMultiselectDropdown*/}
                <AgyleMultiselectAutocomplete
                    options={filterClinicians}
                    onChange={(val) => handleChangeFilter('clinicians', val)}
                    label={t('Clinician')}
                    value={selectedFiltersSelector?.clinicians}
                    size="medium"
                    hideOption
                    noBorder={false}
                    pathToItemValue="__id"
                    fullObjecValue={true}
                    style={{ minWidth: 175 }}
                    showSelectAll={false}
                    selectNoneLabel={'Clear'}
                />
            </Grid>
            <Grid item xs={6} sm={4} md={2} className={classes.filterItemContainer}>
                <AgyleMultiselectAutocomplete
                    options={filterRoles}
                    onChange={(val) => handleChangeFilter('practitionerRoles', val)}
                    label={t('Clinician Role')}
                    value={selectedFiltersSelector?.practitionerRoles}
                    size="medium"
                    hideOption
                    noBorder={false}
                    pathToItemValue="__id"
                    fullObjecValue={true}
                    style={{ minWidth: 175 }}
                    showSelectAll={false}
                    selectNoneLabel={'Clear'}
                />
            </Grid>
            {!isCalendarViewOpen && !patientContext && (
                <Grid item xs={6} sm={4} md={2} className={classes.filterItemContainer}>
                    {/*<AgyleMultiselectDropdown*/}
                    <AgyleMultiselectAutocomplete
                        options={STATUS_FILTER_DICT}
                        onChange={(val) => handleChangeFilter('statuses', val)}
                        label={t('Status')}
                        value={selectedFiltersSelector?.statuses}
                        size="medium"
                        noBorder={false}
                        pathToItemValue="__id"
                        fullObjecValue={true}
                        style={{ minWidth: 175 }}
                        showSelectAll={false}
                        selectNoneLabel={'Clear'}
                    />
                </Grid>
            )}

            {isListViewOpen && !patientContext && (
                <Grid item>
                    <AgyleDictionaryField
                        size="small"
                        noBorder={false}
                        value={sortValue}
                        placeholder={t('Sort by')}
                        options={SORT_BY_OPTIONS}
                        getOptionLabel={renderOptionLabel}
                        style={{ minWidth: 225, maxWidth: 250, justifySelf: 'flex-end' }}
                        onChange={(_, val) => _changeSortState(_, val)}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        optionStyle={classes.dropDownSortStyle}
                        sx={{
                            '.MuiInputBase-input::placeholder': {
                                opacity: 1,
                                color: (theme) => (theme.palette.mode === 'light' ? '#000' : '#FFFFFF'),
                            },

                            '& .MuiInputLabel-root': {
                                opacity: 1,
                                color: (theme) => (theme.palette.mode === 'light' ? '#000' : '#FFFFFF'),
                            },
                        }}
                        // renderOption={(option) => (
                        //     <Typography key={option.key} className={classes.dropDownSortStyle}>{option.key}</Typography>
                        // )}
                    />
                </Grid>
            )}
        </Grid>
        // </Grid>
    );
});

FilterSection.propTypes = {};

FilterSection.defaultProps = {};

export default FilterSection;
